import type { FormAnswers } from '@church/nextjs-ui/website/forms';
import type { FormResponse, SubmitResult } from './types';
import { getMpWidgetToken } from './campus';
import { mpFetch } from '@church/nextjs-ui/website/mp-session';

// Vite bakes these at build time via `define` in vite.config.ts. The slug is
// what lets apps/api attribute writes to this widget's own service identity
// (see the public-write path in apps/api/src/middleware.ts).
const BAKED_API_KEY = process.env.CHURCH_HUB_API_KEY ?? '';
const BAKED_APP_SLUG = process.env.CHURCH_HUB_APP_SLUG ?? '';

function headers(json = false): Record<string, string> {
  const h: Record<string, string> = {};
  if (BAKED_API_KEY) h['x-api-key'] = BAKED_API_KEY;
  if (BAKED_APP_SLUG) h['x-app-slug'] = BAKED_APP_SLUG;
  const token = getMpWidgetToken();
  if (token) h.Authorization = `Bearer ${token}`;
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

const base = (apiBaseUrl: string) => apiBaseUrl.replace(/\/$/, '');

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number
  ) {
    super(message);
  }
}

const RETRY_DELAYS_MS = [1500, 3000];
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** GET with two retries on a 5xx or network error: a brief edge hiccup should not show an error card. */
async function getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    if (attempt > 0) await sleep(RETRY_DELAYS_MS[attempt - 1]);
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    try {
      const res = await mpFetch(url, { headers: headers(), signal });
      if (res.ok) return (await res.json()) as T;
      lastError = new ApiError(`${url} responded ${res.status}`, res.status);
      if (res.status < 500) break;
    } catch (err) {
      if (signal?.aborted) throw err;
      lastError = err;
    }
  }
  throw lastError;
}

/** POST once. A response is never retried blind: two rows are worse than an error. */
async function postJson<T>(url: string, body: unknown): Promise<T> {
  const res = await mpFetch(url, {
    method: 'POST',
    headers: headers(true),
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => null)) as (T & { error?: string }) | null;
  if (!res.ok) throw new ApiError(data?.error || `${url} responded ${res.status}`, res.status);
  return data as T;
}

export const is404 = (err: unknown) => err instanceof ApiError && err.status === 404;

export function fetchForm(
  apiBaseUrl: string,
  ref: string,
  signal?: AbortSignal
): Promise<FormResponse> {
  return getJson(`${base(apiBaseUrl)}/api/forms/${encodeURIComponent(ref)}`, signal);
}

export function submitForm(
  apiBaseUrl: string,
  ref: string,
  input: {
    person: { firstName: string; lastName: string; email: string; phone: string };
    answers: FormAnswers;
    website: string;
  }
): Promise<SubmitResult> {
  return postJson(`${base(apiBaseUrl)}/api/forms/${encodeURIComponent(ref)}/responses`, input);
}
